import React, { useEffect, useMemo, useRef } from 'react'
import { t } from '../i18n'
import { feedAdRules } from '../Utils/ads'
import { track } from '../Utils/analytics'
import { dayLabel, localDay } from '../Utils/time'
import AdSlot from './AdSlot'
import { Spinner } from './Icons'
import StoryCard from './StoryCard'

/* Placeholder cards while a feed loads (spec 6: skeletons, not spinners). */
export function SkeletonCards({ count = 4, main = false }) {
  return (
    <div aria-busy="true" aria-label={t('common.loading')}>
      {main && (
        <div className="feed-item skeleton-card">
          <div className="sk sk-line sk-meta" />
          <div className="sk sk-line sk-big" />
          <div className="sk sk-line sk-big sk-short" />
          <div className="sk sk-box" />
        </div>
      )}
      {Array.from({ length: count }).map((_, i) => (
        <div className="feed-item skeleton-card" key={i}>
          <div className="sk sk-line sk-meta" />
          <div className="sk sk-line" />
          <div className="sk sk-line sk-short" />
          <div className="sk sk-line sk-footer" />
        </div>
      ))}
    </div>
  )
}

export function FeedError({ onRetry }) {
  return (
    <div className="feed-message" role="alert">
      <p>{t('feed.error')}</p>
      <button type="button" className="btn btn-secondary" onClick={onRetry}>
        {t('common.retry')}
      </button>
    </div>
  )
}

/* Loads the next page when the end of the list comes near. */
function LoadMore({ feed }) {
  const ref = useRef(null)
  const { loadMore, nextCursor, loadingMore, moreError } = feed
  useEffect(() => {
    if (!nextCursor || !ref.current || moreError) return undefined
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), {
      rootMargin: '800px 0px',
    })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [nextCursor, loadMore, moreError, feed.items.length])

  if (!nextCursor) return null
  return (
    <div ref={ref} className="load-more">
      {moreError ? (
        <button type="button" className="btn btn-secondary" onClick={loadMore}>
          {t('common.retry')}
        </button>
      ) : (
        loadingMore && <Spinner size={22} />
      )}
    </div>
  )
}

const DEPTHS = [5, 10, 20, 40]

/* feed_depth at the 5th, 10th, 20th and 40th card and feed_end_reached, once
   per load each (analytics spec 5.1): cards carry data-depth, the end line
   data-feed-end. */
function useDepthEvents(feed, ready) {
  const sent = useRef({ epoch: -1, depths: new Set(), end: false })
  useEffect(() => {
    if (!ready) return undefined
    if (sent.current.epoch !== feed.adEpoch) {
      sent.current = { epoch: feed.adEpoch, depths: new Set(), end: false }
    }
    const props = feed.eventProps ? feed.eventProps() : {}
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target
        if (el.dataset.feedEnd) {
          if (sent.current.end) return
          sent.current.end = true
          track('feed_end_reached', { surface: props.surface, depth: Number(el.dataset.feedEnd) })
          return
        }
        const depth = Number(el.dataset.depth)
        if (sent.current.depths.has(depth)) return
        sent.current.depths.add(depth)
        track('feed_depth', { surface: props.surface, chip: props.chip, depth })
      })
    })
    document.querySelectorAll('[data-depth], [data-feed-end]').forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [feed, ready])
}

/* Ads (ads spec 2.1): the first after `firstAfter` cards, the main story
   counting when the page shows one (mainShown), then every `every` cards, up
   to `max` per load. Stories brought in by the pill don't count, and an ad is
   never the last item before the next page loads. adSurface: the config's
   "surfaces" switch this feed follows; none = no ads. */
export default function FeedList({
  feed,
  adSurface = null,
  mainShown = false,
  empty = null,
  skeletonMain = false,
  hideTopic = null,
  daySeparators = false,
  tagFor = null,
}) {
  // Taken once per load, so a config change never moves what's on screen.
  const adRules = useMemo(
    () => (adSurface ? feedAdRules(adSurface) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adSurface, feed.adEpoch]
  )
  useDepthEvents(feed, feed.status === 'ready' && feed.items.length > 0)

  if (feed.status === 'loading') return <SkeletonCards count={5} main={skeletonMain} />
  if (feed.status === 'error') return <FeedError onRetry={feed.retry} />
  if (feed.items.length === 0) {
    return feed.mainStory ? null : (
      <div className="feed-message">{empty || <p>{t('feed.empty')}</p>}</div>
    )
  }

  const rows = []
  let ads = 0
  let untilAd = adRules ? adRules.firstAfter - (mainShown ? 1 : 0) : Infinity
  const addAd = () => {
    if (ads >= adRules.max) return
    ads += 1
    rows.push(
      <AdSlot
        key={`ad-${feed.adEpoch}-${ads}`}
        unit={adRules.unit}
        surface={adSurface}
        position={ads}
      />
    )
    untilAd = adRules.every
  }
  if (adRules && untilAd <= 0) addAd()
  let day = null
  feed.items.forEach((story, i) => {
    // From 1, the main story counting, ads not (analytics spec 5).
    const position = i + 1 + (mainShown ? 1 : 0)
    const isNew = feed.newIds.has(story.id)
    // Newest-first topic pages (articles): "Danas", "Jučer", "Ponedjeljak, 12. 10." (spec 9.3a).
    if (daySeparators && localDay(story.publishedAt) !== day) {
      day = localDay(story.publishedAt)
      rows.push(
        <h2 className="day-separator" key={`day-${day}`}>
          {dayLabel(story.publishedAt)}
        </h2>
      )
    }
    rows.push(
      <div
        className="feed-item"
        key={story.id}
        data-depth={DEPTHS.includes(position) ? position : undefined}
      >
        <StoryCard
          story={story}
          position={position}
          // isFresh: new since the push that opened this topic (spec 9.12).
          isNew={isNew || Boolean(story.isFresh)}
          hideTopic={hideTopic}
          tag={tagFor ? tagFor(story) : undefined}
        />
      </div>
    )
    if (!adRules || feed.adSkipIds.has(story.id)) return
    untilAd -= 1
    if (untilAd === 0 && i < feed.items.length - 1) addAd()
  })

  const days = feed.extra?.windowDays
  return (
    <>
      {rows}
      <LoadMore feed={feed} />
      {/* The end of what we show, instead of a load that finds nothing. */}
      {!feed.nextCursor && days > 0 && (
        <p className="feed-end" data-feed-end={feed.items.length + (mainShown ? 1 : 0)}>
          {t('feed.endOfList', { count: days })}
        </p>
      )}
    </>
  )
}
